import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { X, Save, CheckCircle, AlertCircle } from "lucide-react";
import { formatDateForInput } from "@/utils/dateUtils";
import { useIdValidation, formatSouthAfricanId } from "@/utils/saIdValidation";
import { showError } from "@/utils/toastHelpers";

export default function BloodTestForm({ test, onSubmit, onClose }) {
  const [formData, setFormData] = useState(
    test
      ? {
          ...test,
          test_date: formatDateForInput(test.test_date),
          results: test.results || {},
        }
      : {
          patient_identification_number: "",
          patient_email: "",
          test_date: "",
          test_type: "",
          lab_name: "",
          doctor_name: "",
          notes: "",
          results: {},
        }
  );

  // SA ID validation
  const idValidation = useIdValidation(formData.patient_identification_number);

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate SA ID before submitting
    if (formData.patient_identification_number && !idValidation.isValid) {
      showError("Invalid South African ID number", idValidation.errors.join(', '));
      return;
    }

    onSubmit(formData);
  };

  const handleInputChange = (field, value) => {
    // Format ID number as user types
    if (field === 'patient_identification_number') {
      // Remove non-digits and limit to 13 characters
      const cleanValue = value.replace(/\D/g, '').substring(0, 13);
      setFormData((prev) => ({
        ...prev,
        [field]: cleanValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleResultChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      results: {
        ...prev.results,
        [field]: parseFloat(value) || value,
      },
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-4xl bg-white/90 backdrop-blur-xl border-slate-200/60 shadow-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>
            {test ? "Edit Blood Test" : "Add Blood Test Results"}
          </CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="patient_identification_number" className="flex items-center gap-2">
                  South African ID Number *
                  {formData.patient_identification_number && (
                    idValidation.isValid ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : !idValidation.isEmpty && (
                      <AlertCircle className="w-4 h-4 text-red-500" />
                    )
                  )}
                </Label>
                <div className="relative">
                  <Input
                    id="patient_identification_number"
                    value={formData.patient_identification_number}
                    onChange={(e) =>
                      handleInputChange("patient_identification_number", e.target.value)
                    }
                    placeholder="9001015009087"
                    required
                    maxLength={13}
                    className={`${
                      formData.patient_identification_number && !idValidation.isEmpty
                        ? idValidation.isValid
                          ? 'border-green-300 focus:border-green-400'
                          : 'border-red-300 focus:border-red-400'
                        : ''
                    }`}
                  />
                  {formData.patient_identification_number && formData.patient_identification_number.length === 13 && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs font-mono text-slate-500">
                      {formatSouthAfricanId(formData.patient_identification_number)}
                    </div>
                  )}
                </div>
                
                {/* Validation feedback */}
                {formData.patient_identification_number && !idValidation.isEmpty && (
                  <div className="space-y-1">
                    {idValidation.isValid ? (
                      <div className="text-xs text-green-600">
                        <p className="flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          Valid South African ID number
                        </p>
                        {idValidation.details && (
                          <div className="ml-4 text-slate-600">
                            Gender: {idValidation.details.gender}, 
                            DOB: {idValidation.details.dateOfBirth.day}/{idValidation.details.dateOfBirth.month}/{idValidation.details.dateOfBirth.year}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-xs text-red-600">
                        {idValidation.errors.map((error, index) => (
                          <p key={index} className="flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {error}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                <p className="text-xs text-slate-500">
                  Patient&apos;s 13-digit South African ID number
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="patient_email">Patient Email (Optional)</Label>
                <Input
                  id="patient_email"
                  type="email"
                  value={formData.patient_email}
                  onChange={(e) =>
                    handleInputChange("patient_email", e.target.value)
                  }
                  placeholder="Enter patient email (if available)"
                />
                <p className="text-xs text-slate-500">
                  Used for notification and fallback identification
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="test_date">Test Date</Label>
                <Input
                  id="test_date"
                  type="date"
                  value={formData.test_date}
                  onChange={(e) =>
                    handleInputChange("test_date", e.target.value)
                  }
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="test_type">Test Type</Label>
                <Select
                  value={formData.test_type}
                  onValueChange={(value) =>
                    handleInputChange("test_type", value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select test type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="complete_blood_count">
                      Complete Blood Count
                    </SelectItem>
                    <SelectItem value="basic_metabolic_panel">
                      Basic Metabolic Panel
                    </SelectItem>
                    <SelectItem value="lipid_panel">Lipid Panel</SelectItem>
                    <SelectItem value="liver_function">
                      Liver Function
                    </SelectItem>
                    <SelectItem value="thyroid_function">
                      Thyroid Function
                    </SelectItem>
                    <SelectItem value="hemoglobin_a1c">
                      Hemoglobin A1C
                    </SelectItem>
                    <SelectItem value="vitamin_d">Vitamin D</SelectItem>
                    <SelectItem value="b12_folate">B12 & Folate</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="lab_name">Laboratory</Label>
                <Input
                  id="lab_name"
                  value={formData.lab_name}
                  onChange={(e) =>
                    handleInputChange("lab_name", e.target.value)
                  }
                  placeholder="Lab name"
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="doctor_name">Doctor Name</Label>
                <Input
                  id="doctor_name"
                  value={formData.doctor_name}
                  onChange={(e) =>
                    handleInputChange("doctor_name", e.target.value)
                  }
                  placeholder="Ordering physician"
                />
              </div>
            </div>

            {/* Test Results */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-slate-900">
                Test Results
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Glucose (mg/dL)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.results?.glucose || ""}
                    onChange={(e) =>
                      handleResultChange("glucose", e.target.value)
                    }
                    placeholder="70-100"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Hemoglobin (g/dL)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.results?.hemoglobin || ""}
                    onChange={(e) =>
                      handleResultChange("hemoglobin", e.target.value)
                    }
                    placeholder="12-16"
                  />
                </div>

                <div className="space-y-2">
                  <Label>White Blood Cells (/µL)</Label>
                  <Input
                    type="number"
                    value={formData.results?.white_blood_cells || ""}
                    onChange={(e) =>
                      handleResultChange("white_blood_cells", e.target.value)
                    }
                    placeholder="4500-11000"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Total Cholesterol (mg/dL)</Label>
                  <Input
                    type="number"
                    value={formData.results?.cholesterol_total || ""}
                    onChange={(e) =>
                      handleResultChange("cholesterol_total", e.target.value)
                    }
                    placeholder="< 200"
                  />
                </div>

                <div className="space-y-2">
                  <Label>HDL Cholesterol (mg/dL)</Label>
                  <Input
                    type="number"
                    value={formData.results?.cholesterol_hdl || ""}
                    onChange={(e) =>
                      handleResultChange("cholesterol_hdl", e.target.value)
                    }
                    placeholder="> 40"
                  />
                </div>

                <div className="space-y-2">
                  <Label>LDL Cholesterol (mg/dL)</Label>
                  <Input
                    type="number"
                    value={formData.results?.cholesterol_ldl || ""}
                    onChange={(e) =>
                      handleResultChange("cholesterol_ldl", e.target.value)
                    }
                    placeholder="< 100"
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => handleInputChange("notes", e.target.value)}
                placeholder="Additional notes or observations..."
                rows={3}
              />
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-gradient-to-r from-blue-600 to-indigo-600"
              >
                <Save className="w-4 h-4 mr-2" />
                {test ? "Save Changes" : "Save Test Results"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
